// services
import AxiosInstanceCreator from 'services/api';

// models
import type {
  ReqRAManagementSearchProps,
  ResRAManagementAttachmentsProps,
  ResRAManagementDetailProps,
  ResRAManagementRegisterProps,
  ResRAManagementSearchProps
} from '../models';

const riskAssessmentInstance = new AxiosInstanceCreator({
  baseURL: `/v1/api/risk-assessment/management`
}).create();

export const riskAssessmentManagementApi = {
  /**
   * 위험성 평가서 페이지 검색 API
   *
   * RA ISSUE 위험성평가서 페이지를 검색합니다.
   * - DRAFT 문서는 내가 작성한 것만 조회 가능합니다.
   * [UAE_GSIL] Risk Assessment_PC_화면설계서(v1.0)_24.10.24
   * Page: 11
   * ID: RA_W_01
   * Role: COMMON
   *
   * @param {ReqRAManagementSearchProps} params
   */
  async getRiskAssessmentManagement(
    params: ReqRAManagementSearchProps
  ): Promise<ResRAManagementSearchProps> {
    return await riskAssessmentInstance
      .get<ResRAManagementSearchProps>(``, { params })
      .then((res) => res.data);
  },

  /**
   * 위험성 평가서 상세 조회 API
   *
   * RA ISSUE 위험성평가서 상세 내용을 조회합니다.
   * - 조회한 사용자가 해당 문서에 대해 결재했거나, 현재 결재 대기 중인 경우 myApprovalStatus 값이 null 이 아닌 값으로 지정됩니다.
   * [UAE_GSIL] Risk Assessment_PC_화면설계서(v1.0)_24.10.24
   * Page: 11
   * ID: RA_WP_01, RA_WP_04
   * Role: COMMON
   */
  async getRiskAssessmentManagementDetail(
    riskAssessmentId: string
  ): Promise<ResRAManagementDetailProps> {
    return await riskAssessmentInstance
      .get<ResRAManagementDetailProps>(`/${riskAssessmentId}`)
      .then((res) => res.data);
  },

  /**
   * 위험성 평가서 등록 API
   *
   * RA ISSUE 위험성평가서 작성하여, 임시저장(status=Draft) 또는 결재 상신(status=Ongoing)합니다.
   * [UAE_GSIL] Risk Assessment_PC_화면설계서(v1.0)_24.10.24
   * Page: 11
   * ID: RA_WP_01, RA_WP_04
   * Role: COMMON
   *
   * @param {FormData} data
   */
  async postRiskAssessmentManagement(data: FormData): Promise<ResRAManagementRegisterProps> {
    return await riskAssessmentInstance
      .post(``, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((res) => res.data);
  },

  /**
   * 위험성 평가서 삭제 API
   *
   * RA ISSUE 위험성평가서를 삭제합니다.
   * - 임시 저장 항목만 삭제가능합니다.
   * - 위험성 평가서가 Ongoing 상태인 경우 해당 위험성 평가서를 삭제할 수 없습니다.
   * - SUMMIT(상신)된 항목은 RETRIVE(회수)하여 DRAFT 상태로 만들고 삭제가 가능합니다.
   * - RETRIVE(회수)는 다음 결재자의 결재가 이루어지기 전까지만 가능합니다.
   * [UAE_GSIL] Risk Assessment_PC_화면설계서(v1.0)_24.10.24
   * Page: 11
   * ID: RA_WP_01
   * Role: COMMON
   */
  async deleteRiskAssessmentManagement(riskAssessmentId: string): Promise<void> {
    return await riskAssessmentInstance.delete(`/${riskAssessmentId}`).then((res) => res.data);
  },

  /**
   * 위험성 평가서 첨부파일 조회 API
   *
   * RA ISSUE 위험성평가서 첨부파일을 조회합니다.
   */
  async getRiskAssessmentManagementAttachments(
    riskAssessmentId: number
  ): Promise<ResRAManagementAttachmentsProps> {
    return await riskAssessmentInstance
      .get(`/${riskAssessmentId}/attachments`)
      .then((res) => res.data);
  },

  async downloadRiskAssessmentManagementAttachment(fileId: string): Promise<void> {
    return await riskAssessmentInstance.get(`/downloads/${fileId}`).then((res) => res.data);
  }
};
